// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import axios from 'axios';
import { stringify } from 'query-string';
import * as CONSTANTS from '../../constants/index';
import { getValidationErrors } from '../../utils';
import { getConfig } from "../../reducers/portalConfig";
import { AXIOS_DEFAULT_OPTIONS } from '../';
import { isEmpty } from 'lodash';
import { APIS_ADD_ACCESS, APIS_REMOVE_ACCESS, ORGANIZATIONS_ADD_ACCESS, ORGANIZATIONS_REMOVE_ACCESS } from '../../pages/ApiPlans/Details/utils';

export const fetchApiPlansList = ({ size, page } = {}) => async (dispatch, getState) => {
  const config = getConfig(getState());
  const { portal } = config;
  const params = stringify({
    page,
    size,
    sort: 'name,asc',
  })
  const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/api-plans?${params}`;

  dispatch({ type: CONSTANTS.API_PLANS_REQUEST });

  await axios.get(url, AXIOS_DEFAULT_OPTIONS)
    .then(response => dispatch({ type: CONSTANTS.API_PLANS_SUCCESS, payload: response.data }))
    .catch((error) => dispatch({
      type: CONSTANTS.API_PLANS_ERROR,
      payload: getValidationErrors(error),
    }));
};

export const enableApiPlans = (payload) => async (dispatch, getState) => {
  const config = getConfig(getState());
  const { portal } = config;
  const url = `${portal.hostname}/api/${portal.tenantPrefix}/tenant-admin/1.0/settings/FEATURE_FLAG_API_PLANS`;

  dispatch({
    type: CONSTANTS.ENABLE_API_PLANS_REQUEST,
  });

  await axios.put(url, payload, AXIOS_DEFAULT_OPTIONS)
    .then(response => dispatch({
      type: CONSTANTS.ENABLE_API_PLANS_SUCCESS,
      payload: response.data,
    }))
    .catch((error) => dispatch({
      type: CONSTANTS.ENABLE_API_PLANS_ERROR, payload: getValidationErrors(error),
    }));
};

export const createApiPlan = (payload) => (
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/api-plans`;
    dispatch({
      type: CONSTANTS.CREATE_API_PLANS_REQUEST,
    });
    await axios.post(url, payload, AXIOS_DEFAULT_OPTIONS)
      .then((response) => {
        dispatch({
          type: CONSTANTS.CREATE_API_PLANS_SUCCESS,
          payload: response.data,
        })
      })
      .catch((error) => {
        dispatch({
          type: CONSTANTS.CREATE_API_PLANS_ERROR,
          payload: getValidationErrors(error),
        })
      })
  }
)

export const fetchApiPlanDetails = (apiPlanUuid) => (
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/api-plans/${apiPlanUuid}`;
    dispatch({
      type: CONSTANTS.FETCH_API_PLANS_DETAILS_REQUEST,
    });
    await axios.get(url, AXIOS_DEFAULT_OPTIONS)
      .then((response) => {
        dispatch({
          type: CONSTANTS.FETCH_API_PLANS_DETAILS_SUCCESS,
          payload: response.data,
        })
      })
      .catch((error) => {
        dispatch({
          type: CONSTANTS.FETCH_API_PLANS_DETAILS_ERROR,
          payload: getValidationErrors(error),
        })
      })
  }
)


export const editApiPlan = (payload, apiPlanUuid) => (
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/api-plans/${apiPlanUuid}`;
    dispatch({
      type: CONSTANTS.UPDATE_API_PLANS_REQUEST,
    });
    await axios.put(url, payload, AXIOS_DEFAULT_OPTIONS)
      .then((response) => {
        dispatch({
          type: CONSTANTS.UPDATE_API_PLANS_SUCCESS,
          payload: response.data,
        })
      })
      .catch((error) => {
        dispatch({
          type: CONSTANTS.UPDATE_API_PLANS_ERROR,
          payload: getValidationErrors(error),
        })
      })
  }
)


export const fetchApisForApiPlans = (
  apiPlanUuid,
  {
    size,
    page,
    name,
    apiPlanApiAccessStatus,
  } = {},
) => async (dispatch, getState) => {
  const config = getConfig(getState());
  const { portal } = config;
  const params = stringify({
    size,
    page,
    apiPlanApiAccessStatus,
    name: !isEmpty(name) ? name : undefined,
  })
  const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/access/apis-api-plan/${apiPlanUuid}?${params}`;

  await axios.get(url, AXIOS_DEFAULT_OPTIONS)
    .then(response => dispatch({
      type: CONSTANTS.FETCH_APIS_FOR_API_PLANS_SUCCESS,
      payload: response.data,
    }))
    .catch((error) => dispatch({
      type: CONSTANTS.FETCH_APIS_FOR_API_PLANS_ERROR,
      payload: getValidationErrors(error),
    }));
};

export const updateApisForApiPlans = (payload, apiPlanUuid, changeMode) => {
  return async (dispatch, getState) => {
    try {
      const config = getConfig(getState());
      const { portal } = config;
      const action = changeMode === APIS_ADD_ACCESS ? "add" :
        changeMode === APIS_REMOVE_ACCESS ? "remove" : "";

      const params = stringify({ action });

      const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/0.1/api-plans/${apiPlanUuid}/apis?${params}`;

      await axios.patch(url, payload, AXIOS_DEFAULT_OPTIONS);

      dispatch({
        type: CONSTANTS.UPDATE_APIS_FOR_API_PLANS_SUCCESS,
        payload: CONSTANTS.SUCCESS,
      });
    } catch (error) {
      dispatch({
        type: CONSTANTS.UPDATE_APIS_FOR_API_PLANS_ERROR,
        payload: getValidationErrors(error),
      });
    }
  };
};

export const fetchOrganizationsForApiPlans = (
  apiPlanUuid,
  {
    size,
    page,
    orgName,
    apiPlanOrgAccessStatus,
  } = {},
) => async (dispatch, getState) => {
  const config = getConfig(getState());
  const { portal } = config;
  const params = stringify({
    size,
    page,
    apiPlanOrgAccessStatus,
    orgName: !isEmpty(orgName) ? orgName : undefined,
  })
  const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/access/organizations-api-plan/${apiPlanUuid}?${params}`;

  await axios.get(url, AXIOS_DEFAULT_OPTIONS)
    .then(response => dispatch({
      type: CONSTANTS.FETCH_ORGANIZATIONS_FOR_API_PLANS_SUCCESS,
      payload: response.data,
    }))
    .catch((error) => dispatch({
      type: CONSTANTS.FETCH_ORGANIZATIONS_FOR_API_PLANS_ERROR,
      payload: getValidationErrors(error),
    }));
};

export const updateOrganizationsForApiPlans = (payload, apiPlanUuid, changeMode) => {
  return async (dispatch, getState) => {
    try {
      const config = getConfig(getState());
      const { portal } = config;
      const action = changeMode === ORGANIZATIONS_ADD_ACCESS ? "add" :
        changeMode === ORGANIZATIONS_REMOVE_ACCESS ? "remove" : "";

      const params = stringify({ action });

      const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/0.1/api-plans/${apiPlanUuid}/organizations?${params}`;

      await axios.patch(url, payload, AXIOS_DEFAULT_OPTIONS);

      dispatch({
        type: CONSTANTS.UPDATE_ORGANIZATIONS_FOR_API_PLANS_SUCCESS,
        payload: CONSTANTS.SUCCESS,
      });
    } catch (error) {
      dispatch({
        type: CONSTANTS.UPDATE_ORGANIZATIONS_FOR_API_PLANS_ERROR,
        payload: getValidationErrors(error),
      });
    }
  };
};

export const deleteApiPlan = (apiPlanUuid) => (
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/api-plans/${apiPlanUuid}`;

    dispatch({
      type: CONSTANTS.DELETE_API_PLAN_REQUEST,
    });
    await axios.delete(url, AXIOS_DEFAULT_OPTIONS)
      .then(() => {
        dispatch({
          type: CONSTANTS.DELETE_API_PLAN_SUCCESS,
          payload: CONSTANTS.SUCCESS,
        })
      })
      .catch((error) => {
        dispatch({
          type: CONSTANTS.DELETE_API_PLAN_ERROR,
          payload: getValidationErrors(error),
        })
      })
  }
)
